import React, { useEffect } from 'react';
import { OfflineSyncChip } from './components/OfflineSyncChip';
import { installOfflineCache } from './utils/offlineStore';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeatureProvider, useFeatures } from './context/FeatureContext';
import Dashboard from './pages/Dashboard';
import HiveDetail from './pages/HiveDetail';
import InspectionForm from './pages/InspectionForm';
import InspectionView from './pages/InspectionView';
import Analytics from './pages/Analytics';
import CompanyProfile from './pages/CompanyProfile';
import SwarmTraps from './pages/SwarmTraps';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import FindHoney from './pages/FindHoney';
import TrialSignup from './pages/TrialSignup';
import EmailVerification from './pages/EmailVerification';
import UserManagement from './pages/UserManagement';
import MyProfile from './pages/MyProfile';
import Marketplace from './pages/Marketplace';
import AdminListings from './pages/AdminListings';
import HiveMind from './pages/HiveMind';
import UserManual from './pages/UserManual';
import KnowledgeLibrary from './pages/KnowledgeLibrary';
import QueenRearing from './pages/QueenRearing';
import AdminSettings from './pages/AdminSettings';
import Subscription from './pages/Subscription';
import AdminSubscriptionPlans from './pages/AdminSubscriptionPlans';
import AdminConfiguration from './pages/AdminConfiguration';
import Pricing from './pages/Pricing';
import HiveCosts from './pages/HiveCosts';
import BulkAddHives from './pages/BulkAddHives';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CrewManagement from './pages/CrewManagement';
import CrewDashboard from './pages/CrewDashboard';
import CrewLogin from './pages/CrewLogin';
import PlanLanding from './pages/PlanLanding';
import ReferralRewards from './pages/ReferralRewards';
import AdminReferralManagement from './pages/AdminReferralManagement';
import BulkSplit from './pages/BulkSplit';
import AdminBugReports from './pages/AdminBugReports';
import AdminLegalEditor from './pages/AdminLegalEditor';
import AdminReviews from './pages/AdminReviews';
import AdminAds from './pages/AdminAds';
import AdminBeeClubs from './pages/AdminBeeClubs';
import AdvertiserDashboard from './pages/AdvertiserDashboard';
import FindBeeClub from './pages/FindBeeClub';
import ReportSwarm from './pages/ReportSwarm';
import SwarmStatus from './pages/SwarmStatus';
import TeamChat from './pages/TeamChat';
import HiveComparison from './pages/HiveComparison';
import DIYIdeas from './pages/DIYIdeas';
import IdeaDetail from './pages/IdeaDetail';
import BuilderProfile from './pages/BuilderProfile';
import BuilderSettings from './pages/BuilderSettings';
import CollectionDetail from './pages/CollectionDetail';
import BugReportWidget from './components/BugReportWidget';
import { Toaster } from './components/ui/sonner';
import './App.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Component to dynamically update favicon and document title
const AppBranding = () => {
  useEffect(() => {
    const fetchAppSettings = () => {
      // Use XMLHttpRequest to avoid fetch interceptor issues
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${API}/api/app-settings`, true);
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          try {
            const settings = JSON.parse(xhr.responseText);
            
            // Update document title
            if (settings.app_name) {
              document.title = `${settings.app_name} | ${settings.app_tagline || 'Beekeeping Management'}`;
            }
            
            // Update favicon if set
            if (settings.app_favicon) {
              const faviconLink = document.getElementById('app-favicon');
              if (faviconLink) {
                faviconLink.href = `${API}/api/uploads/${settings.app_favicon}`;
              }
            }
          } catch (e) {
            // Silently ignore parse errors
          }
        }
      };
      
      xhr.send();
    };
    
    fetchAppSettings();
  }, []);
  
  return null;
};

// Protected route wrapper - allows demo mode (read-only) or authenticated users
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isDemo, loading, isCrew } = useAuth();
  
  // Show loading while initial auth check is happening
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="text-center">
          <div className="text-4xl mb-4"><img src="/thrive-logo.png" alt="Loading" className="w-12 h-12 mx-auto" /></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Also check localStorage directly for crew tokens (crew navigating to owner pages)
  const hasCrewToken = !!localStorage.getItem('crewToken');
  
  if (!isAuthenticated && !isDemo && !hasCrewToken) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin-only route
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/" replace />;
  
  return children;
};

// Feature-gated route — shows upgrade prompt if feature not available
const FeatureRoute = ({ feature, featureName, children }) => {
  const { hasFeature, loading } = useFeatures();
  if (loading) return null;
  if (!hasFeature(feature)) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4 text-2xl">🔒</div>
          <h2 className="text-xl font-bold text-stone-800 mb-2">{featureName} Requires an Upgrade</h2>
          <p className="text-stone-500 mb-6">This feature isn't available on your current plan. Upgrade to unlock {featureName.toLowerCase()} and more.</p>
          <a href="/subscription" className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
            View Plans & Upgrade
          </a>
        </div>
      </div>
    );
  }
  return children;
};

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname, search]);
  return null;
}

function AppRoutes() {
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/find-honey" element={<FindHoney />} />
      <Route path="/find-bee-club" element={<FindBeeClub />} />
      <Route path="/report-swarm" element={<ReportSwarm />} />
      <Route path="/swarm-status" element={<SwarmStatus />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/trial-signup" element={<TrialSignup />} />
      <Route path="/verify-email" element={<EmailVerification />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/plan/:planId" element={<PlanLanding />} />
      <Route path="/referral-rewards" element={<ProtectedRoute><ReferralRewards /></ProtectedRoute>} />
      <Route path="/admin/referral" element={<ProtectedRoute><AdminReferralManagement /></ProtectedRoute>} />
      <Route path="/bulk-split" element={<Navigate to="/?view=bulk-split" replace />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      {/* Crew routes - separate auth */}
      <Route path="/crew-login" element={<CrewLogin />} />
      <Route path="/crew-dashboard" element={<CrewDashboard />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/analytics" element={<Navigate to="/?view=analytics" replace />} />
      <Route path="/beekeepers" element={<Navigate to="/?view=crew" replace />} />
      <Route path="/crew-management" element={<Navigate to="/?view=crew" replace />} />
      <Route path="/company-profile" element={<ProtectedRoute><CompanyProfile /></ProtectedRoute>} />
      <Route path="/swarm-traps" element={<Navigate to="/?view=swarm-traps" replace />} />
      <Route path="/hive/:hiveId" element={<ProtectedRoute><HiveDetail /></ProtectedRoute>} />
      <Route path="/hive/:hiveId/new-inspection" element={<ProtectedRoute><InspectionForm /></ProtectedRoute>} />
      <Route path="/hive/:hiveId/edit-inspection/:inspectionId" element={<ProtectedRoute><InspectionForm editMode={true} /></ProtectedRoute>} />
      <Route path="/inspection/:inspectionId" element={<ProtectedRoute><InspectionView /></ProtectedRoute>} />
      <Route path="/users" element={<Navigate to="/?view=admin-users" replace />} />
      <Route path="/my-profile" element={<Navigate to="/?view=my-profile" replace />} />
      <Route path="/marketplace" element={<Navigate to="/?view=marketplace" replace />} />
      <Route path="/subscription" element={<Navigate to="/?view=subscription" replace />} />
      <Route path="/admin/listings" element={<Navigate to="/?view=admin-listings" replace />} />
      <Route path="/admin/settings" element={<Navigate to="/?view=admin-settings" replace />} />
      <Route path="/admin/subscription-plans" element={<Navigate to="/?view=admin-plans" replace />} />
      <Route path="/admin/configuration" element={<Navigate to="/?view=admin-config" replace />} />
      <Route path="/admin/bug-reports" element={<Navigate to="/?view=admin-bugs" replace />} />
      <Route path="/admin/legal" element={<Navigate to="/?view=admin-legal" replace />} />
      <Route path="/admin/reviews" element={<Navigate to="/?view=admin-reviews" replace />} />
      <Route path="/admin/ads" element={<Navigate to="/?view=admin-ads" replace />} />
      <Route path="/admin/bee-clubs" element={<Navigate to="/?view=admin-bee-clubs" replace />} />
      {/* Deep-link redirects for sidebar items */}
      <Route path="/apiaries" element={<Navigate to="/?view=apiaries" replace />} />
      <Route path="/hives" element={<Navigate to="/?view=hives" replace />} />
      <Route path="/inspections" element={<Navigate to="/?view=inspections" replace />} />
      <Route path="/tasks" element={<Navigate to="/?view=tasks" replace />} />
      <Route path="/health" element={<Navigate to="/?view=health" replace />} />
      <Route path="/crew" element={<Navigate to="/?view=crew" replace />} />
      <Route path="/rescues" element={<Navigate to="/?view=rescues" replace />} />
      <Route path="/swarms" element={<Navigate to="/?view=swarm-traps" replace />} />
      <Route path="/mite-control" element={<Navigate to="/?view=treatments" replace />} />
      <Route path="/record-weights" element={<Navigate to="/?view=record-weights" replace />} />
      <Route path="/record-harvests" element={<Navigate to="/?view=harvests" replace />} />
      <Route path="/record-sales" element={<Navigate to="/?view=sales" replace />} />
      <Route path="/compare-hives" element={<Navigate to="/?view=hive-comparison" replace />} />
      <Route path="/find-beekeepers" element={<Navigate to="/?view=find-beekeepers" replace />} />
      <Route path="/find-local-honey" element={<Navigate to="/?view=find-honey" replace />} />
      <Route path="/merch-store" element={<Navigate to="/?view=merch-store" replace />} />
      <Route path="/plans" element={<Navigate to="/?view=subscription" replace />} />
      <Route path="/profile" element={<Navigate to="/?view=my-profile" replace />} />
      <Route path="/advertise" element={<AdvertiserDashboard />} />
      <Route path="/team-chat" element={<Navigate to="/?view=team-chat" replace />} />
      <Route path="/queen-rearing" element={<Navigate to="/?view=queen-rearing" replace />} />
      <Route path="/hive-costs" element={<Navigate to="/?view=hive-costs" replace />} />
      <Route path="/hive-comparison" element={<Navigate to="/?view=hive-comparison" replace />} />
      <Route path="/diy-ideas" element={<Navigate to="/?view=diy-ideas" replace />} />
      <Route path="/diy-ideas/:ideaId" element={<ProtectedRoute><IdeaDetail /></ProtectedRoute>} />
      <Route path="/builder/:userId" element={<ProtectedRoute><BuilderProfile /></ProtectedRoute>} />
      <Route path="/builder-settings" element={<Navigate to="/?view=builder-settings" replace />} />
      <Route path="/diy-collections/:collectionId" element={<ProtectedRoute><CollectionDetail /></ProtectedRoute>} />
      <Route path="/bulk-add-hives" element={<Navigate to="/?view=bulk-add-hives" replace />} />
      <Route path="/hive-mind" element={<Navigate to="/?view=hive-mind" replace />} />
      <Route path="/knowledge" element={<Navigate to="/?view=knowledge" replace />} />
      <Route path="/help" element={<Navigate to="/?view=help" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  useEffect(() => { installOfflineCache(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <FeatureProvider>
          <BrowserRouter>
            <AppBranding />
            <AppRoutes />
            <BugReportWidget />
            <OfflineSyncChip />
          </BrowserRouter>
          <Toaster position="top-center" />
        </FeatureProvider>
      </AuthProvider>
    </div>
  );
}

export default App;